import { AppBar } from '#app/components/AppBar/AppBar.js';
import { AppBarLeftProps } from '#app/components/AppBar/components/AppBarLeft.js';
import { NAVBAR_HEIGHT } from '#app/components/AppBar/constants.js';
import { EditFromPortfolioBanner } from '#app/components/EditFromPortfolioBanner/EditFromPortfolioBanner.js';
import { DebugGrid } from '#app/components/Grid/DebugGrid.js';
import { PageLayout } from '#app/components/Grid/PageLayout.js';
import { Logo } from '#app/components/Logo/src/Logo.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { NAVBAR_HEIGHT_SM } from '#app/components/Navigation/components/base/constants.js';
import {
  SIDEBAR_CLOSED_WIDTH,
  SIDEBAR_OPEN_WIDTH,
} from '#app/constants/sidenav.js';
import { useSidebar } from '#app/contexts/SidebarContext/useSidebar.js';
import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useIsSmallScreen } from '#app/hooks/useIsSmallScreen.js';
import { useRouter } from '#app/hooks/useRouter.js';
import { type Theme } from '#app/theme/index.js';
import { ArrowRightIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { lazy, type ReactNode, Suspense } from 'react';
import { css, styled } from 'styled-components';

const Sidebar = lazy(async () => {
  return {
    default: (await import('#app/components/Sidenav/Sidebar.js')).Sidebar,
  };
});

const AdminCMDK = lazy(async () => {
  return {
    default: (await import('#app/components/Cmdk/Cmdk.js')).Cmdk,
  };
});

const PromotionalBanner = lazy(async () => {
  return {
    default: (
      await import('#app/components/PromotionalBanner/PromotionalBanner.js')
    ).PromotionalBanner,
  };
});

const WorkspaceSubscriptionEndedBanner = lazy(async () => {
  return {
    default: (
      await import(
        '#app/features/dashboard/client/WorkspaceSubscriptionEndedBanner/WorkspaceSubscriptionEndedBanner.js'
      )
    ).WorkspaceSubscriptionEndedBanner,
  };
});

const ContraProSubscribeBanner = lazy(async () => {
  return {
    default: (
      await import(
        '#app/components/ContraProSubscribeBanner/ContraProSubscribeBanner.js'
      )
    ).ContraProSubscribeBanner,
  };
});

const C4cReferralBanner = lazy(async () => {
  return {
    default: (
      await import('#app/components/C4cReferralBanner/C4cReferralBanner.js')
    ).C4cReferralBanner,
  };
});

export const MAX_MAIN_LAYOUT_SIZE = 1_440;

type BackgroundColor = keyof Theme['colorsV2'];

// dark theme colors are not yet defined, using placeholders
// todo: add transparent black to design library @julsgud
const getNavbarColors = (theme: Theme, variant: 'dark' | 'light') => {
  return variant === 'dark'
    ? css`
        --nav-background-color: rgba(19, 19, 19, 0.36);
        --nav-border-color: ${theme.colorsV2.gray60};
        --nav-item-color: ${theme.colorsV2.white};
        --nav-item-hover-color: ${theme.colorsV2.gray90};
        --nav-item-icon-color: ${theme.colorsV2.gray60};
      `
    : css`
        --nav-background-color: ${theme.colors.white40};
        --nav-mobile-menu-color: ${theme.colorsV2.gray05};
        --nav-border-color: ${theme.colorsV2.white};
        --nav-item-color: ${theme.colorsV2.black};
        --nav-item-muted-color: ${theme.colorsV2.gray60};
        --nav-item-hover-color: ${theme.colorsV2.gray05};
        --nav-item-focus-color: ${theme.colorsV2.gray20};
        --nav-item-icon-color: ${theme.colorsV2.gray70};
      `;
};

const Container = styled.div<{
  $backgroundColor?: BackgroundColor;
  $backgroundImage?: string;
  $backgroundPosition?: string;
  $backgroundSize?: string;
}>`
  ${({
    $backgroundColor = 'white',
    $backgroundImage,
    $backgroundPosition = 'bottom left',
    $backgroundSize = 'cover',
    theme,
  }) => css`
    ${getNavbarColors(theme, 'light')}
    position: relative;
    min-height: 100%;
    background-color: ${theme.colorsV2[$backgroundColor]};

    ${$backgroundImage &&
    css`
      background-image: url(${$backgroundImage});
      background-repeat: no-repeat;
      background-position: ${$backgroundPosition};
      background-size: ${$backgroundSize};
    `}
  `}
`;

const PageContainer = styled.div<{
  $isSidebarEnabled: boolean;
  $noMinHeight?: boolean;
}>`
  ${({ $isSidebarEnabled, $noMinHeight, theme }) => css`
    display: flex;
    flex: 1;
    min-height: ${$noMinHeight ? '0vh' : `calc(100vh - ${NAVBAR_HEIGHT}px)`};
    transition: padding-top ease-in-out 300ms;
    ${$isSidebarEnabled
      ? css`
          padding-top: ${NAVBAR_HEIGHT_SM}px;
          ${theme.mediaQueries.sm} {
            padding-top: ${NAVBAR_HEIGHT}px;
          }
        `
      : css`
          flex-flow: row nowrap;
        `};
  `}
`;

const ContentContainer = styled(PageLayout)<{
  $containerColor?: BackgroundColor;
  $isSidebarExpanded: boolean;
  $noMarginOnTheBottom?: boolean;
  $noMarginOnTheTop?: boolean;
  $noMaxWidth?: boolean;
  $widthOffset?: number;
}>`
  ${({
    $containerColor,
    $isSidebarExpanded,
    $noMarginOnTheBottom,
    $noMarginOnTheTop,
    $noMaxWidth,
    theme,
  }) => css`
    --sidebar-width: ${$isSidebarExpanded
      ? SIDEBAR_OPEN_WIDTH
      : SIDEBAR_CLOSED_WIDTH}px;

    flex: 1;
    max-width: ${$noMaxWidth ? '100%' : `${MAX_MAIN_LAYOUT_SIZE}px`};
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    background-color: ${$containerColor
      ? theme.colorsV2[$containerColor]
      : 'transparent'};
    position: relative;

    margin-top: ${$noMarginOnTheTop ? 0 : 12}px;

    ${theme.mediaQueries.md} {
      margin-top: ${$noMarginOnTheTop ? 0 : 24}px;
      width: calc(100% - var(--sidebar-width));
    }

    @media screen and (min-width: ${MAX_MAIN_LAYOUT_SIZE}px) {
      margin-bottom: ${$noMarginOnTheBottom ? 0 : 32}px;
      border-radius: 30px;
    }
  `}
`;

const LogoContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    height: 64px;
    padding: 0 24px;
    position: absolute;

    ${theme.mediaQueries.md} {
      height: 80px;
      padding: 0 36px;
    }
  `}
`;

const SidebarSpacer = styled.div<{ open?: boolean }>`
  ${({ open }) => css`
    background: #14171f;
    transition: width 200ms cubic-bezier(0.4, 0.9, 0.2, 1) 0ms;
    width: ${open ? SIDEBAR_OPEN_WIDTH : SIDEBAR_CLOSED_WIDTH}px;
    position: sticky;
    inset: 0;
    z-index: 1000;
    will-change: width;
  `}
`;

const StyledRightArrowIcon = styled(ArrowRightIcon)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.white};
  `}
`;

type MainLayoutProps = {
  readonly backgroundColor?: BackgroundColor;
  readonly backgroundImage?: string;
  readonly backgroundPosition?: string;
  readonly backgroundSize?: string;
  /* A prop that is passed to the `AppBar` component to handle the contextual banner. */
  readonly banner?: ReactNode;
  readonly children: ReactNode;
  readonly containerColor?: BackgroundColor;
  readonly hideAppBarBorderAtMd?: boolean;
  readonly hideNavbar?: boolean;
  readonly noMarginOnTheBottom?: boolean;
  readonly noMarginOnTheTop?: boolean;
  readonly noMaxWidth?: boolean;
  readonly noMinHeight?: boolean;
  readonly routeLabelOverride?: AppBarLeftProps['routeLabelOverride'];
  readonly username?: string;
};

export const MainLayout = ({
  backgroundColor,
  backgroundImage,
  backgroundPosition,
  backgroundSize,
  banner,
  children,
  containerColor,
  hideAppBarBorderAtMd,
  hideNavbar = false,
  noMarginOnTheBottom,
  noMarginOnTheTop,
  noMaxWidth,
  noMinHeight,
  routeLabelOverride,
  username,
}: MainLayoutProps) => {
  const isMobile = useIsMobile();
  const { isActive } = useRouter();
  const sidebar = useSidebar();

  const currentUser = useCurrentUser();
  const shouldShowSidebar = Boolean(currentUser?.id);
  const isSmallScreen = useIsSmallScreen();

  const { openModal: openLogoutModal } = useModal({
    identifier: 'LogoutModal',
    modalProps: {},
  });

  const { playwrightTest } = useAppConfiguration();

  const isClientHomeRoute = isActive(getRoute('/client/home', {}), false);

  return (
    <Container
      $backgroundColor={backgroundColor}
      $backgroundImage={backgroundImage}
      $backgroundPosition={backgroundPosition}
      $backgroundSize={backgroundSize}
    >
      {currentUser ? <AdminCMDK /> : null}

      {currentUser && isClientHomeRoute ? (
        <Suspense fallback={null}>
          <WorkspaceSubscriptionEndedBanner />
        </Suspense>
      ) : null}

      {currentUser ? (
        <Suspense fallback={null}>
          <ContraProSubscribeBanner
            isSidenavDocked={
              (sidebar.desktop?.isExpanded || sidebar.mobile?.isOpen) ?? false
            }
          />
        </Suspense>
      ) : null}

      <EditFromPortfolioBanner
        isSidenavDocked={
          (sidebar.desktop?.isExpanded || sidebar.mobile?.isOpen) ?? false
        }
      />

      <Suspense fallback={null}>
        <C4cReferralBanner
          isSidenavDocked={
            (sidebar.desktop?.isExpanded || sidebar.mobile?.isOpen) ?? false
          }
        />
      </Suspense>

      <>
        {hideNavbar ? (
          <LogoContainer>
            <Logo condensed={isSmallScreen} />
          </LogoContainer>
        ) : (
          <AppBar
            banner={banner}
            hideAppBarBorderAtMd={hideAppBarBorderAtMd}
            isContractor={currentUser?.selectedUserType === 'CONTRACTOR'}
            promotionalBanner={
              isMobile ? null : (
                <Suspense fallback={null}>
                  <PromotionalBanner
                    backgroundGradient={[
                      '#467CC4 7.93%',
                      '#9473B4 34.12%',
                      '#8E85D7 55.61%',
                      '#6A57E3 92.21%',
                    ]}
                    href="https://bit.ly/creator-tools-2024-banner"
                    icon={StyledRightArrowIcon}
                    isSidenavDocked={
                      (sidebar.desktop?.isExpanded || sidebar.mobile?.isOpen) ??
                      false
                    }
                    minHeight={72}
                    textContent="Cast your vote for the Creator Tools Awards"
                    trackingLinkName="creator_tools_award_banner"
                    uiFlag="HIDE_CREATOR_TOOLS_AWARD_BANNER"
                  />
                </Suspense>
              )
            }
            routeLabelOverride={routeLabelOverride}
            username={username}
          />
        )}
      </>
      <PageContainer
        $isSidebarEnabled={Boolean(playwrightTest?.name)}
        $noMinHeight={noMinHeight}
      >
        {shouldShowSidebar && !hideNavbar && !isMobile ? (
          <div>
            <SidebarSpacer open={sidebar.desktop?.isExpanded} />
            <Sidebar openLogoutModal={openLogoutModal} />
          </div>
        ) : null}

        {sidebar.mobile?.isOpen ? (
          <Suspense>
            <Sidebar openLogoutModal={openLogoutModal} />
          </Suspense>
        ) : null}

        <ContentContainer
          $containerColor={containerColor}
          $isSidebarExpanded={
            (sidebar.desktop?.isExpanded || sidebar.mobile?.isOpen) ?? false
          }
          $noMarginOnTheBottom={noMarginOnTheBottom}
          $noMarginOnTheTop={noMarginOnTheTop}
          $noMaxWidth={noMaxWidth}
          widthOffset={0}
        >
          <DebugGrid />
          {children}
        </ContentContainer>
      </PageContainer>
    </Container>
  );
};
